import { useSubmit } from "@certn/api";
import {
  DropdownMenu,
  GhostIconButton,
  MenuItem,
  MenuSection,
} from "@certn/cascadia-react";
import { showToast, Text } from "@certn/cascadia";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface CustomQuestionnaireActionsDropdownProps {
  id: string;
  isActive: boolean;
  hideDeactivate?: boolean;
  updateTemplates: () => void;
}

export function CustomQuestionnaireActionsDropdown({
  id,
  isActive,
  hideDeactivate = true,
  updateTemplates,
}: CustomQuestionnaireActionsDropdownProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const editTemplate = () =>
    navigate(`/settings/custom-questionnaire-templates/${id}`);

  const { submit: activateTemplate, isSubmitting: isSubmittingActivate } =
    useSubmit(
      `/api/internal/client-portal/custom-questionnaire-templates/${id}/activate/`,
      {
        showToastOnError: true,
        onSuccess: async () => {
          showToast(t("Template successfully activated!"), {
            type: "success",
          });
          updateTemplates();
        },
      }
    );
  const { submit: deactivateTemplate, isSubmitting: isSubmittingDeactivate } =
    useSubmit(
      `/api/internal/client-portal/custom-questionnaire-templates/${id}/deactivate/`,
      {
        showToastOnError: true,
        onSuccess: async () => {
          showToast(t("Template successfully deactivated!"), {
            type: "success",
          });
          updateTemplates();
        },
      }
    );

  const disabledKeys = [
    ...(isSubmittingActivate ? ["activate-template"] : []),
    ...(isSubmittingDeactivate ? ["deactivate-template"] : []),
  ];

  return (
    <DropdownMenu
      disabledKeys={disabledKeys}
      triggerButton={
        <GhostIconButton
          testID="custom-questionnaire-templates-item-expand-button"
          size="small"
          icon="more_horiz"
          aria-label={t("Expand menu")}
        />
      }
      placement="bottom right"
      onItemAction={async (key) => {
        if (key === "edit-template") {
          editTemplate();
        } else if (key === "deactivate-template") {
          await deactivateTemplate();
        } else if (key === "activate-template") {
          await activateTemplate();
        }
      }}
    >
      <MenuSection>
        <MenuItem
          id="edit-template"
          testID="edit-custom-questionnaire-template"
        >
          <Text style="input/text-field">{t("Edit")}</Text>
        </MenuItem>
        {!hideDeactivate && isActive && (
          <MenuItem
            id="deactivate-template"
            testID="deactivate-custom-questionnaire-template"
          >
            <Text color="text-danger" style="input/text-field">
              {t("Deactivate")}
            </Text>
          </MenuItem>
        )}
        {!isActive && (
          <MenuItem
            id="activate-template"
            testID="activate-custom-questionnaire-template"
          >
            <Text style="input/text-field">{t("Activate")}</Text>
          </MenuItem>
        )}
      </MenuSection>
    </DropdownMenu>
  );
}
